import { PropsWithChildren, useEffect } from "react";
import { Box, Grid } from "styled-system/jsx";
import {
  ErrorBoundaryPage,
  LoadingPage,
  MaintenanceNoticeBanner,
  MaintenancePage,
  RootHeader,
} from "shared/components";
import { ErrorBoundary } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { EnumUserStatus, useUserUpdateMutation } from "gql/generated";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppContext } from "AppContext";
import { createMultilingual } from "shared/utils";
import { useMaintenance } from "./useMaintenance.ts";

export const RootLayout = ({ children }: PropsWithChildren) => {
  const { user } = useAuth0();
  const { dbUser, isForcingLogout, refetchDbUser } = useAppContext();
  const [updateUserStatus] = useUserUpdateMutation();
  const {
    maintenanceData,
    maintenanceLoading,
    maintenanceError,
    showMaintenanceBanner,
    showMaintenanceDoorstop,
    handleClose,
    getHashMessage,
  } = useMaintenance();

  // Ensure the user is updated correctly
  useEffect(() => {
    // Check the user's status property and update it to ENROLLED if it is current PROVISIONED
    const checkUser = async () => {
      try {
        if (dbUser && dbUser._id && dbUser.status !== EnumUserStatus.Enrolled) {
          await updateUserStatus({
            variables: {
              id: dbUser._id,
              record: {
                status: EnumUserStatus.Enrolled,
              },
            },
          });
          await refetchDbUser();
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      }
    };
    checkUser();
  }, [dbUser, user, refetchDbUser, updateUserStatus]);

  if (isForcingLogout || maintenanceLoading) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <LoadingPage />
      </Grid>
    );
  }

  if (maintenanceError) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <ErrorBoundaryPage />
      </Grid>
    );
  }

  if (showMaintenanceDoorstop) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <MaintenancePage
          message={
            maintenanceData?.maintenanceFindOne?.doorstop.message ??
            createMultilingual("")
          }
        />
      </Grid>
    );
  }

  return (
    <Grid
      gridTemplateRows={showMaintenanceBanner ? "64px 73px 1fr" : "0 73px 1fr"}
      height={"svh"}
      backgroundColor={"bg.wa.lightest"}
      rowGap={0}
    >
      <MaintenanceNoticeBanner
        message={
          maintenanceData?.maintenanceFindOne?.banner.message ??
          createMultilingual("")
        }
        hashMessage={getHashMessage()}
        open={showMaintenanceBanner}
        onClose={handleClose}
      />
      <RootHeader />
      <ErrorBoundary fallback={<ErrorBoundaryPage />}>
        <Box overflow={"hidden"}>{children}</Box>
      </ErrorBoundary>
    </Grid>
  );
};
